/**
 * 运营策略列表页（整合SKU管理）
 * 
 * 重构后结构:
 * - hooks/useOperationStrategyList.ts: 业务逻辑
 * - components/SKUListColumns.tsx: 表格列定义
 */

import React from 'react';
import { Table, Card, Button, Modal, Form, Input, Select } from 'antd';
import { RocketOutlined, PlusOutlined } from '@ant-design/icons';
import { PageHeader } from '../../components/Layout/PageHeader';
import { useOperationStrategyList } from './hooks';
import { getSKUColumns } from './components/SKUListColumns';
import './OperationStrategy.css';

export const OperationStrategyList: React.FC = () => {
    const [createForm] = Form.useForm();

    const {
        loading,
        skuList,
        createModalVisible,
        setCreateModalVisible,
        handleViewStrategy,
        handleCreateSKU,
        handleDeleteSKU,
        // Edit
        editModalVisible,
        setEditModalVisible,
        handleEditSKU,
        handleUpdateSKU,
    } = useOperationStrategyList({ createForm });

    // 获取表格列配置
    const columns = getSKUColumns({
        onView: handleViewStrategy,
        onDelete: handleDeleteSKU,
        onEdit: handleEditSKU,
    });

    return (
        <div className="operation-strategy-page">
            <PageHeader
                title="运营策略"
                subtitle="SKU运营策略分析与管理"
                icon={<RocketOutlined />}
                breadcrumbs={[
                    { title: '首页', href: '/' },
                    { title: '运营策略' },
                ]}
                actions={[
                    {
                        key: 'create',
                        label: '新建SKU',
                        type: 'primary',
                        icon: <PlusOutlined />,
                        onClick: () => setCreateModalVisible(true),
                    },
                ]}
            />

            <div className="operation-strategy-content">
                <Card bordered={false}>
                    <Table
                        columns={columns}
                        dataSource={skuList || []}
                        rowKey="id"
                        loading={loading}
                        pagination={{
                            pageSize: 20,
                            showSizeChanger: true,
                            showTotal: (total) => `共 ${total} 个SKU`,
                        }}
                        scroll={{ x: 1200 }}
                    />
                </Card>
            </div>

            {/* 新建SKU弹窗 */}
            <Modal
                title="新建SKU"
                open={createModalVisible}
                onOk={handleCreateSKU}
                onCancel={() => {
                    setCreateModalVisible(false);
                    createForm.resetFields();
                }}
                confirmLoading={loading}
            >
                <Form form={createForm} layout="vertical">
                    <Form.Item
                        name="name"
                        label="SKU名称"
                        rules={[{ required: true, message: '请输入SKU名称' }]}
                    >
                        <Input placeholder="输入唯一的SKU名称" />
                    </Form.Item>
                    <Form.Item name="asin" label="ASIN">
                        <Input placeholder="可选，填写ASIN" />
                    </Form.Item>
                    <Form.Item name="sku_code" label="SKU编码">
                        <Input placeholder="可选，填写SKU编码" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 编辑SKU弹窗 */}
            <Modal
                title="编辑SKU"
                open={editModalVisible}
                onOk={handleUpdateSKU}
                onCancel={() => {
                    setEditModalVisible(false);
                    createForm.resetFields();
                }}
                confirmLoading={loading}
            >
                <Form form={createForm} layout="vertical">
                    <Form.Item
                        name="name"
                        label="SKU名称"
                        rules={[{ required: true, message: '请输入SKU名称' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item name="asin" label="Main ASIN">
                        <Input />
                    </Form.Item>
                    <Form.Item name="sku_code" label="SKU编码">
                        <Input />
                    </Form.Item>
                    <Form.Item name="status" label="状态" rules={[{ required: true }]}>
                        <Select>
                            <Select.Option value="developing">开发中 (Developing)</Select.Option>
                            <Select.Option value="sample">样品测试 (Sample)</Select.Option>
                            <Select.Option value="active">在售 (Active)</Select.Option>
                            <Select.Option value="inactive">停售 (Inactive)</Select.Option>
                            <Select.Option value="clearance">清仓 (Clearance)</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default OperationStrategyList;
